import React from 'react';
import classnames from 'classnames';
import { isNullOrUndefined } from 'utils/object-utils';
import { TooltipOptions } from 'models/utils/common/tooltip-options-model';
import { LEFT_PANELS } from './panel-constants';
import {
  AUTOMATION_CREATION_TYPE,
  WORKPAPER_TYPE,
} from './element-panel-constants';
import { ReactComponent as AutomatedTag } from 'icons/automated-tag.svg';
import ToolTip from 'components/common/tool-tip-component';
import { STATEMENT_NAV_TABS } from 'constants/feature/statement-navigator-constants';

export const ELEMENT_HIGHLIGHT_STATES = {
  DEFAULT: 'default',
  POST_BATCH: 'post-batch',
  OPENED: 'opened',
  SELECTED_FORMULA_ROW: 'selected-formula-row',
  PANEL_SELECTED: 'panel-selected',
  HOVERED_FROM_FORMULA: 'hovered-from-formula',
  HOVERED_FROM_STATEMENT_CONTENT: 'hovered-from-statement-content',
  SELECTED_FOR_COPY_FORMULA: 'selected-for-copy-formula',
  FORMULA_PASTE_SUCCESS: 'formula-paste-success',
  FORMULA_PASTE_FAILURE: 'formula-paste-failure',
  ELEMENT_MAP_SUCCESS: 'element-map-success',
  ELEMENT_MAP_FAILURE: 'element-map-failure',
  CALLOUT_DRAG_FAILURE: 'callout-drag-failure',
};

const ANNOTATION_DETAILS_BLOCK = 'annotation-details';
export const ANNOTATION_BASE_BLOCK = 'element-annotation';

export const ELEMENT_BLOCK = 'tieout-element';
export const COMFORT_ASSIGN_ELEMENT = 'comfort-assign-element';
export const SIDE_BY_SIDE_VIEW_SELECTED_ELEMENT =
  'selected-element-for-side-by-side-view';
export const TIEOUT_ELEMENT_LEFT = 'tieout-element-left';

export const ANNOTATION_ICON_SIZE = '10px';
export const CALLOUT_MAX_WIDTH = '120px';
export const ElementSelectionLimit = 250;
export const CROSS_ICON_SIZE = 20;

export const hoverCopyFormulaDisabledTooltip = new TooltipOptions({
  text: 'element-panel.annotation.content.copy-formula.disabled.tool-tip',
  id: 'batch-panel.checkbox.copy-formula-disable-tooltip-id',
  position: 'bottom',
});

const AUTOMATED_TAG_WIDTH = 14;
const AUTOMATED_TAG_HEIGHT = 8;

const handleMouseEnter = (domElementId) => {
  // Target the parent element that contains the tooltip
  const parentElement = document.getElementById(`${domElementId}`);
  if (parentElement) {
    parentElement.removeAttribute('data-tooltip-id');
    parentElement.removeAttribute('data-for');
    parentElement.removeAttribute('data-tip');
  }
  // Access tooltip-root element which is outside the main React root
  const tooltipRoot = document.getElementById('tooltip-root');
  if (tooltipRoot) {
    const tooltipElement = tooltipRoot.querySelector(`#${domElementId}`);
    if (tooltipElement) {
      tooltipElement.style.display = 'none';
      tooltipElement.style.visibility = 'hidden';
    }
  }
};

const handleMouseLeave = (domElementId) => {
  // Restore tooltip attributes to the parent element
  const parentElement = document.getElementById(`${domElementId}`);
  if (parentElement) {
    parentElement.setAttribute('data-tooltip-id', domElementId);
    parentElement.setAttribute('data-for', domElementId);
    parentElement.setAttribute('data-tip', 'data-tip');
  }

  // Access tooltip-root element and restore tooltip visibility
  const tooltipRoot = document.getElementById('tooltip-root');
  if (tooltipRoot) {
    const tooltipElement = tooltipRoot.querySelector(`#${domElementId}`);
    if (tooltipElement) {
      tooltipElement.style.display = '';
      tooltipElement.style.visibility = '';
    }
  }
};

const automatedTagTooltipWithIcon = (domElementId) => (
  <ToolTip
    id={`${ANNOTATION_BASE_BLOCK}__annotation-automation`}
    text={'tieout.elements.automatically.identified'}
    position={'top'}
  >
    <AutomatedTag
      className={`${ANNOTATION_BASE_BLOCK}-automated-icon`}
      width={AUTOMATED_TAG_WIDTH}
      height={AUTOMATED_TAG_HEIGHT}
      tabIndex={-1}
      onMouseEnter={() => handleMouseEnter(domElementId)}
      onMouseLeave={() => handleMouseLeave(domElementId)}
    />
  </ToolTip>
);

const getElementInternalReferenceGroupName = ({
  elementDetails,
  fontSize,
  customCSS = false,
}) => {
  if (
    isNullOrUndefined(elementDetails) ||
    !elementDetails.hasInternalReferenceGroup()
  ) {
    return null;
  } else {
    return (
      <span
        style={{ fontSize: `${fontSize}px` }}
        className={classnames(
          ANNOTATION_BASE_BLOCK,
          `${ANNOTATION_BASE_BLOCK}-internal-reference-group`,
          elementDetails.isInternalRefFlagged() &&
            `${ANNOTATION_BASE_BLOCK}-internal-reference-group--flagged`,
          { [`${ANNOTATION_DETAILS_BLOCK}__contents`]: customCSS },
        )}
      >
        {elementDetails.internalReferenceGroupName}
      </span>
    );
  }
};

export const checkIfWorkpaperisTrialBalance = (workPaperType) => {
  return workPaperType === WORKPAPER_TYPE.OMNIA_TRIAL_BALANCE;
};

export const getWorkpapersWithoutTrialBalance = (workpaperList) => {
  return workpaperList.filter(
    (wp) => !checkIfWorkpaperisTrialBalance(wp.split('|')[1]),
  );
};

export const getFlaggedWorkpapers = (elementDetails) => {
  const creationTypeMap = elementDetails?.workpaperReferenceCreationTypeMap;

  if (!creationTypeMap || Object.keys(creationTypeMap).length === 0) {
    const workpapers = elementDetails?.workpaperReferenceNumberList || [];

    return {
      flaggedWorkpapers: new Set(
        workpapers
          .filter((wp) => {
            const [_, wpType, wpFlagged] = wp.split('|');
            return (
              wpType !== WORKPAPER_TYPE.OMNIA_TRIAL_BALANCE &&
              wpFlagged === 'true'
            );
          })
          .map((wp) => wp.split('|')[0]),
      ),
    };
  }

  return {
    flaggedWorkpapers: new Set(
      Object.values(creationTypeMap)
        .flat()
        .filter((ref) => {
          const [_, wpType, wpFlagged] = ref.split('|');
          return (
            wpType !== WORKPAPER_TYPE.OMNIA_TRIAL_BALANCE &&
            wpFlagged === 'true'
          );
        })
        .map((ref) => ref.split('|')[0]),
    ),
  };
};

export const renderWorkpaperListOnStatementContent = (
  elementDetails,
  isElementFilterPanel,
  workpapersSelected,
) => {
  if (isNullOrUndefined(elementDetails)) {
    return null;
  }

  const { flaggedWorkpapers } = getFlaggedWorkpapers(elementDetails);

  const workpaperList = getWorkpapersWithoutTrialBalance(
    elementDetails.workpaperReferenceNumberList,
  );

  // Filter and map workpapers to display data
  const displayData = workpaperList
    .map((wp) => {
      const isWPEndsWithTrue = wp.split('|')[2] === 'true';
      const wpWithoutBooleanFlag = wp.split('|')[0];

      // Check flagging conditions
      const hasAnyTrueStatus = isElementFilterPanel
        ? isWPEndsWithTrue
        : flaggedWorkpapers.has(wpWithoutBooleanFlag);
      const isElementFlagged = elementDetails.isWorkpaperFlagged?.() || false;
      const isWPFlagged = hasAnyTrueStatus && isElementFlagged;

      let workpapersToDisplay;
      if (workpapersSelected?.length) {
        workpapersToDisplay = workpapersSelected.includes(wpWithoutBooleanFlag)
          ? wpWithoutBooleanFlag
          : '';
      } else {
        workpapersToDisplay = wpWithoutBooleanFlag;
      }

      if (!workpapersToDisplay) {
        return null;
      }

      return {
        wp,
        workpapersToDisplay,
        isWPFlagged,
      };
    })
    .filter((item) => item !== null);

  // Render with correct comma logic based on filtered items
  return displayData.map((item, i) => {
    const isLast = i === displayData.length - 1;
    return item.isWPFlagged ? (
      <span key={item.wp} className={`${ANNOTATION_BASE_BLOCK}__wp--flagged`}>
        {item.workpapersToDisplay}
        {!isLast ? ', ' : ''}
      </span>
    ) : (
      <React.Fragment key={item.wp}>
        {item.workpapersToDisplay + (isLast ? '' : ', ')}
      </React.Fragment>
    );
  });
};

const getElementWorkpaperContentDisplay = ({
  elementDetails,
  currLeftPanel,
  fontSize,
  customCSS = false,
  shouldRestrictAutomaticIdentifier = false,
  domElementId = null,
  selectedTab,
  workpapersSelected,
}) => {
  const isWorkpaperPanel = currLeftPanel === LEFT_PANELS.WORKPAPER;
  const isElementFilterPanel = selectedTab === STATEMENT_NAV_TABS.elements;

  if (isNullOrUndefined(elementDetails) || !elementDetails.hasWorkpapers()) {
    return null;
  } else {
    const workPaperTitle = getWorkpapersWithoutTrialBalance(
      elementDetails.workpaperReferenceNumberList,
    )
      .map((wp) => wp.split('|')[0])
      .join(', ');

    return (
      <span
        style={{
          fontSize: `${fontSize}px`,
        }}
        title={isWorkpaperPanel ? null : workPaperTitle}
        className={classnames(
          ANNOTATION_BASE_BLOCK,
          `${ANNOTATION_BASE_BLOCK}-workpaper-reference`,
          {
            [`${ANNOTATION_DETAILS_BLOCK}__contents`]: customCSS,
            [`${ANNOTATION_BASE_BLOCK}-workpaper-automated`]: isWorkpaperPanel,
          },
        )}
      >
        {isWorkpaperPanel && domElementId && shouldRestrictAutomaticIdentifier
          ? Object.entries(
              elementDetails?.workpaperReferenceCreationTypeMap || {},
            )
              .flatMap(([creationType, workpaperCodes]) => {
                return getWorkpapersWithoutTrialBalance(workpaperCodes)
                  .map((code, codeIdx) => {
                    let icon = null;
                    if (
                      shouldRestrictAutomaticIdentifier &&
                      Object.values(AUTOMATION_CREATION_TYPE).includes(
                        creationType,
                      )
                    ) {
                      icon = automatedTagTooltipWithIcon(domElementId);
                    }

                    const [wp = '', _, wpFlaged = ''] = code?.split('|') || [];
                    const isElementFlagged =
                      elementDetails.isWorkpaperFlagged?.() || false;
                    const isWPFlagged = wpFlaged === 'true' && isElementFlagged;

                    let workpapersToDisplay;
                    if (workpapersSelected?.length) {
                      workpapersToDisplay = workpapersSelected.includes(wp)
                        ? wp
                        : '';
                    } else {
                      workpapersToDisplay = wp;
                    }

                    if (!workpapersToDisplay) {
                      return null;
                    }

                    return {
                      key: `${creationType}-${codeIdx}`,
                      workpapersToDisplay,
                      icon,
                      isWPFlagged,
                    };
                  })
                  .filter((item) => item !== null);
              })
              .map((item, idx, filteredArr) => {
                const isLast = idx === filteredArr.length - 1;
                return (
                  <span
                    key={item.key}
                    className={classnames({
                      [`${ANNOTATION_BASE_BLOCK}__wp--flagged`]:
                        item.isWPFlagged,
                      [`${ANNOTATION_BASE_BLOCK}-with-icon`]: isWorkpaperPanel,
                    })}
                  >
                    {item.workpapersToDisplay}
                    {item.icon}
                    {!isLast ? ', ' : ''}
                  </span>
                );
              })
          : renderWorkpaperListOnStatementContent(
              elementDetails,
              isElementFilterPanel,
              workpapersSelected,
            )}
      </span>
    );
  }
};

const getElementTickmarksContentDisplay = ({
  elementDetails,
  fontSize,
  customCSS = false,
  currLeftPanel,
  shouldRestrictAutomaticIdentifier = false,
  domElementId = null,
}) => {
  const isTickmarkPanel = currLeftPanel === LEFT_PANELS.TICKMARK;
  if (isNullOrUndefined(elementDetails) || !elementDetails.hasTickmarks()) {
    return null;
  } else {
    return (
      <span
        style={{ fontSize: `${fontSize}px` }}
        className={classnames(
          ANNOTATION_BASE_BLOCK,
          `${ANNOTATION_BASE_BLOCK}-tickmark`,
          { [`${ANNOTATION_DETAILS_BLOCK}__contents`]: customCSS },
        )}
      >
        {isTickmarkPanel && domElementId && shouldRestrictAutomaticIdentifier
          ? Object.entries(elementDetails?.tickmarkCreationTypeMap || {})
              .map(([creationType, tickmarkCodes], index, arr) => {
                return tickmarkCodes.map((code, codeIdx) => {
                  let icon = null;
                  if (
                    shouldRestrictAutomaticIdentifier &&
                    Object.values(AUTOMATION_CREATION_TYPE).includes(
                      creationType,
                    )
                  ) {
                    icon = automatedTagTooltipWithIcon(domElementId);
                  }
                  const isLastType = index === arr.length - 1;
                  const isLastCode = codeIdx === tickmarkCodes.length - 1;
                  return (
                    <span
                      key={`${creationType}-${codeIdx}`}
                      className={classnames({
                        [`${ANNOTATION_BASE_BLOCK}-with-icon`]: isTickmarkPanel,
                      })}
                    >
                      {`${code}`}
                      {icon}
                      {!isLastType || !isLastCode ? ' ' : ''}
                    </span>
                  );
                });
              })
              .flat()
          : customCSS
            ? elementDetails.tickmarks.replace(/}{/g, '},{')
            : elementDetails.tickmarks}
      </span>
    );
  }
};

const getElementTickmarkTextContentDisplay = ({ elementDetails }) => {
  if (isNullOrUndefined(elementDetails) || !elementDetails.hasTickmarkText()) {
    return null;
  } else {
    return (
      <span
        className={classnames(
          ANNOTATION_BASE_BLOCK,
          `${ANNOTATION_BASE_BLOCK}-tickmark`,
        )}
      >
        {elementDetails.tickmarkText}
      </span>
    );
  }
};

const getElementComfortLetterContentDisplay = ({
  elementDetails,
  fontSize,
  customCSS = false,
}) => {
  if (
    isNullOrUndefined(elementDetails) ||
    !elementDetails.hasComfortLetters()
  ) {
    return null;
  } else {
    return (
      <span
        style={{ fontSize: `${fontSize}px` }}
        className={classnames(
          ANNOTATION_BASE_BLOCK,
          `${ANNOTATION_BASE_BLOCK}-comfort-letter-label`,
          { [`${ANNOTATION_DETAILS_BLOCK}__contents`]: customCSS },
        )}
      >
        {customCSS
          ? elementDetails.comfortLetterLabelList
              .map((label) => `[${label}]`)
              .join(', ')
          : elementDetails.comfortLetterLabelList
              .map((label) => `[${label}]`)
              .join('')}
      </span>
    );
  }
};

const getElementFormulaTypeContentDisplay = ({ elementDetails, fontSize }) => {
  const fontSizePx = `${fontSize}px`;
  const FORMULA_ANNOTATION_BLOCK = `${ANNOTATION_BASE_BLOCK}-formula`;
  if (isNullOrUndefined(elementDetails) || !elementDetails.hasFormula()) {
    return null;
  } else {
    return (
      <>
        {elementDetails.hasFootedFormula() && (
          <span
            style={{ fontSize: fontSizePx }}
            className={classnames(
              ANNOTATION_BASE_BLOCK,
              FORMULA_ANNOTATION_BLOCK,
              elementDetails.hasFootedFormulaFlagged()
                ? `${FORMULA_ANNOTATION_BLOCK}--flagged`
                : null,
            )}
          >
            F
          </span>
        )}
        {elementDetails.hasCrossFootedFormula() && (
          <span
            style={{ fontSize: fontSizePx }}
            className={classnames(
              ANNOTATION_BASE_BLOCK,
              FORMULA_ANNOTATION_BLOCK,
              elementDetails.hasCrossFootedFormulaFlagged()
                ? `${FORMULA_ANNOTATION_BLOCK}--flagged`
                : null,
            )}
          >
            Cf
          </span>
        )}
        {elementDetails.hasRecalcFormula() && (
          <span
            style={{ fontSize: fontSizePx }}
            className={classnames(
              ANNOTATION_BASE_BLOCK,
              FORMULA_ANNOTATION_BLOCK,
              elementDetails.hasRecalcFormulaFlagged()
                ? `${FORMULA_ANNOTATION_BLOCK}--flagged`
                : null,
            )}
          >
            Rx
          </span>
        )}
      </>
    );
  }
};

const getElementPanelFormulaContentDisplay = ({
  elementDetails,
  customCSS = false,
}) => {
  const FORMULA_ANNOTATION_BLOCK = `${ANNOTATION_BASE_BLOCK}-formula`;
  if (isNullOrUndefined(elementDetails) || !elementDetails.hasFormula()) {
    return null;
  } else {
    let formulaContent = [];
    const formulaMapping = {
      hasFootedFormula: 'F',
      hasCrossFootedFormula: 'Cf',
      hasRecalcFormula: 'Rx',
    };

    for (const [method, label] of Object.entries(formulaMapping)) {
      if (elementDetails[method]()) {
        formulaContent.push(label);
      }
    }

    if (formulaContent.length === 0) {
      return null;
    }

    return (
      <span
        className={classnames(
          ANNOTATION_BASE_BLOCK,
          FORMULA_ANNOTATION_BLOCK,
          elementDetails.hasRecalcFormulaFlagged()
            ? `${FORMULA_ANNOTATION_BLOCK}--flagged`
            : null,
          { [`${ANNOTATION_DETAILS_BLOCK}__contents`]: customCSS },
        )}
      >
        {formulaContent.join(', ')}
      </span>
    );
  }
};

export const ANNOTATION_DISPLAY_FUNCTIONS = {
  workpaper: getElementWorkpaperContentDisplay,
  internalRef: getElementInternalReferenceGroupName,
  tickmarks: getElementTickmarksContentDisplay,
  tickmarkText: getElementTickmarkTextContentDisplay,
  formula: getElementFormulaTypeContentDisplay,
  comfortLetter: getElementComfortLetterContentDisplay,
  elementFormula: getElementPanelFormulaContentDisplay,
};
